(() => {
  function initBuildCarousels(root = document) {
    root.querySelectorAll('[data-bvm-product-carousel]').forEach((carousel) => {
      if (carousel.dataset.bvmReady === 'true') return;
      carousel.dataset.bvmReady = 'true';

      const viewport = carousel.querySelector('[data-bvm-carousel-track]');
      const prev = carousel.querySelector('[data-bvm-carousel-prev]');
      const next = carousel.querySelector('[data-bvm-carousel-next]');
      const firstCard = carousel.querySelector(
        '.bvm-build-article__product-card'
      );

      if (!viewport || !prev || !next || !firstCard) return;

      function getStep() {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const track = carousel.querySelector(
          '.bvm-build-article__product-track'
        );
        const gap = track
          ? parseFloat(getComputedStyle(track).gap || '0')
          : 0;

        return Math.max(cardWidth + gap, viewport.clientWidth * 0.82);
      }

      function updateButtons() {
        const maxScroll = Math.max(
          0,
          viewport.scrollWidth - viewport.clientWidth
        );

        prev.disabled = viewport.scrollLeft <= 4;
        next.disabled = viewport.scrollLeft >= maxScroll - 4;
      }

      prev.addEventListener('click', () => {
        viewport.scrollBy({
          left: -getStep(),
          behavior: 'smooth'
        });
      });

      next.addEventListener('click', () => {
        viewport.scrollBy({
          left: getStep(),
          behavior: 'smooth'
        });
      });

      let ticking = false;

      viewport.addEventListener(
        'scroll',
        () => {
          if (ticking) return;

          ticking = true;

          requestAnimationFrame(() => {
            updateButtons();
            ticking = false;
          });
        },
        { passive: true }
      );

      window.addEventListener('resize', updateButtons, {
        passive: true
      });

      updateButtons();
    });
  }

  function initRelatedBuildCarousels(root = document) {
    root.querySelectorAll('[data-bvm-related-builds]').forEach((carousel) => {
      if (carousel.dataset.bvmRelatedReady === 'true') return;
      carousel.dataset.bvmRelatedReady = 'true';

      const viewport = carousel.querySelector('[data-bvm-related-track]');
      const prev = carousel.querySelector('[data-bvm-related-prev]');
      const next = carousel.querySelector('[data-bvm-related-next]');
      const controls = carousel.querySelector(
        '.bvm-build-article__related-controls'
      );
      const firstCard = carousel.querySelector('.bvm-build-related-card');
      const track = carousel.querySelector(
        '.bvm-build-article__related-track'
      );

      if (!viewport || !prev || !next || !firstCard || !track) return;

      function getStep() {
        const cardWidth = firstCard.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(track).gap || '0');

        return Math.max(cardWidth + gap, viewport.clientWidth * 0.84);
      }

      function updateButtons() {
        const maxScroll = Math.max(
          0,
          viewport.scrollWidth - viewport.clientWidth
        );

        const hasOverflow = maxScroll > 4;

        if (controls) {
          controls.hidden = !hasOverflow;
        }

        prev.disabled = !hasOverflow || viewport.scrollLeft <= 4;
        next.disabled =
          !hasOverflow || viewport.scrollLeft >= maxScroll - 4;
      }

      prev.addEventListener('click', () => {
        viewport.scrollBy({
          left: -getStep(),
          behavior: 'smooth'
        });
      });

      next.addEventListener('click', () => {
        viewport.scrollBy({
          left: getStep(),
          behavior: 'smooth'
        });
      });

      let ticking = false;

      viewport.addEventListener(
        'scroll',
        () => {
          if (ticking) return;

          ticking = true;

          requestAnimationFrame(() => {
            updateButtons();
            ticking = false;
          });
        },
        { passive: true }
      );

      window.addEventListener('resize', updateButtons, {
        passive: true
      });

      carousel.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', updateButtons, {
            once: true
          });
        }
      });

      updateButtons();
    });
  }

  function initBuildVideos(root = document) {
    root.querySelectorAll('[data-bvm-build-video]').forEach((videoBlock) => {
      if (videoBlock.dataset.bvmVideoReady === 'true') return;
      videoBlock.dataset.bvmVideoReady = 'true';

      const trigger = videoBlock.querySelector('[data-bvm-video-trigger]');
      const thumbnail = videoBlock.querySelector(
        '[data-bvm-video-thumbnail]'
      );
      const player = videoBlock.querySelector(
        '.bvm-build-article__video-player'
      );
      const videoType = videoBlock.dataset.videoType;

      if (!trigger || !player || !videoType) return;

      if (videoType === 'shopify') {
        const template = videoBlock.querySelector(
          '[data-bvm-shopify-video-template]'
        );

        if (!template) {
          videoBlock.hidden = true;
          return;
        }

        trigger.addEventListener(
          'click',
          () => {
            const fragment = template.content.cloneNode(true);
            const video = fragment.querySelector('video');

            player.replaceChildren(fragment);

            if (video) {
              const playPromise = video.play();

              if (
                playPromise &&
                typeof playPromise.catch === 'function'
              ) {
                playPromise.catch(() => {});
              }
            }
          },
          { once: true }
        );

        return;
      }

      if (videoType !== 'youtube') {
        videoBlock.hidden = true;
        return;
      }

      const videoUrl = videoBlock.dataset.videoUrl;

      if (!thumbnail || !videoUrl) {
        videoBlock.hidden = true;
        return;
      }

      function getYouTubeId(url) {
        try {
          const parsedUrl = new URL(url, window.location.origin);
          const hostname = parsedUrl.hostname
            .replace('www.', '')
            .toLowerCase();

          if (hostname === 'youtu.be') {
            return parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
          }

          if (
            hostname === 'youtube.com' ||
            hostname === 'm.youtube.com' ||
            hostname === 'youtube-nocookie.com'
          ) {
            if (parsedUrl.pathname === '/watch') {
              return parsedUrl.searchParams.get('v') || '';
            }

            const parts = parsedUrl.pathname
              .split('/')
              .filter(Boolean);

            if (
              parts[0] === 'embed' ||
              parts[0] === 'shorts' ||
              parts[0] === 'live'
            ) {
              return parts[1] || '';
            }
          }
        } catch (error) {
          return '';
        }

        return '';
      }

      const videoId = getYouTubeId(videoUrl);

      if (!videoId) {
        videoBlock.hidden = true;
        return;
      }

      const encodedVideoId = encodeURIComponent(videoId);

      thumbnail.src =
        `https://i.ytimg.com/vi/${encodedVideoId}/maxresdefault.jpg`;

      thumbnail.addEventListener(
        "error",
        () => {
          thumbnail.src =
            `https://i.ytimg.com/vi/${encodedVideoId}/hqdefault.jpg`;
        },
        { once: true }
      );

      trigger.addEventListener(
        'click',
        () => {
          const iframe = document.createElement('iframe');

          iframe.src =
            `https://www.youtube-nocookie.com/embed/${encodedVideoId}` +
            '?autoplay=1&rel=0';

          iframe.title =
            trigger.getAttribute('aria-label') || window.BV_T("bonvent_studio.other_shared_ui.build_video_15b00171b1","Build video");

          iframe.allow =
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

          iframe.allowFullscreen = true;
          iframe.referrerPolicy = 'strict-origin-when-cross-origin';

          player.replaceChildren(iframe);
        },
        { once: true }
      );
    });
  }

  function initAll(root = document) {
    initBuildCarousels(root);
    initRelatedBuildCarousels(root);
    initBuildVideos(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();